/**
 * Feedback Constants
 *
 * Rating and category options for feedback collection.
 */

import {
  AlertCircle,
  Bug,
  CheckCircle2,
  Frown,
  HelpCircle,
  Lightbulb,
  Meh,
  Smile,
} from '@tonnie/assets';
import type { ComponentType } from 'react';

export interface IFeedbackFile {
  id: string;
  file: File;
  preview?: string;
  size: number;
  type: string;
}

export interface IFeedbackData {
  rating: number | null;
  category: string | null;
  description: string;
  files: IFeedbackFile[];
  context?: {
    page: string;
    hero?: string;
  };
}

export interface IRatingOption {
  value: number;
  icon: ComponentType<{ className?: string }>;
  /** i18n key resolved via `t()` at render time. */
  labelKey: string;
  color: string;
}

export interface ICategoryOption {
  value: string;
  icon: ComponentType<{ className?: string }>;
  /** i18n key resolved via `t()` at render time. */
  labelKey: string;
  /** i18n key resolved via `t()` at render time. */
  descriptionKey: string;
}

export const RATING_OPTIONS: IRatingOption[] = [
  {
    value: 1,
    icon: Frown,
    labelKey: 'feedback.rating.veryDissatisfied',
    color: 'text-destructive-accessible',
  },
  {
    value: 2,
    icon: Frown,
    labelKey: 'feedback.rating.dissatisfied',
    color: 'text-warning-accessible',
  },
  { value: 3, icon: Meh, labelKey: 'feedback.rating.neutral', color: 'text-warning-accessible' },
  { value: 4, icon: Smile, labelKey: 'feedback.rating.satisfied', color: 'text-info-accessible' },
  {
    value: 5,
    icon: Smile,
    labelKey: 'feedback.rating.verySatisfied',
    color: 'text-success-accessible',
  },
];

export const CATEGORY_OPTIONS: ICategoryOption[] = [
  {
    value: 'bug',
    icon: Bug,
    labelKey: 'feedback.category.bug.label',
    descriptionKey: 'feedback.category.bug.description',
  },
  {
    value: 'suggestion',
    icon: Lightbulb,
    labelKey: 'feedback.category.suggestion.label',
    descriptionKey: 'feedback.category.suggestion.description',
  },
  {
    value: 'question',
    icon: HelpCircle,
    labelKey: 'feedback.category.question.label',
    descriptionKey: 'feedback.category.question.description',
  },
  {
    value: 'praise',
    icon: CheckCircle2,
    labelKey: 'feedback.category.praise.label',
    descriptionKey: 'feedback.category.praise.description',
  },
  {
    value: 'other',
    icon: AlertCircle,
    labelKey: 'feedback.category.other.label',
    descriptionKey: 'feedback.category.other.description',
  },
];

export const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Create initial feedback state
 */
export function createInitialFeedback(): IFeedbackData {
  return {
    rating: null,
    category: null,
    description: '',
    files: [],
  };
}
